import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import TransactionHistoryService from '../../../services/transactionHistory.service';
import { toast } from 'react-toastify';
import { translateErrorMessage } from '../../../utils';

function mapDocToTx(doc) {
  if (!doc) return null;
  return {
    _id: doc._id,
    chain: doc.chain,
    walletAddress: doc.walletAddress,
    hash: doc.hash,
    createdAt: doc.createdAt ? new Date(doc.createdAt) : new Date(),
    updatedAt: doc.updatedAt ? new Date(doc.updatedAt) : new Date(),
    status: doc.status,
    action: doc.action,
    metadata: doc.metadata,
    link: doc.link,
    index: doc.index,
  };
}

const initialState = {
  history: {},
  status: 'idle',
};

function getSuccessPayload(response) {
  return response?.data?.data;
}

function parseListPayload(payload) {
  if (Array.isArray(payload)) {
    return { rows: payload, total: payload.length };
  }
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return { rows: payload.data, total: payload.total ?? payload.data.length };
  }
  return { rows: [], total: 0 };
}

export const fetchTransactionHistory = createAsyncThunk(
  'transactionHistory/fetch',
  async ({ chain, walletAddress }, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      const response = await TransactionHistoryService.list({ chain, walletAddress });
      dispatch(slice.actions.handleStatus('success'));
      const payload = getSuccessPayload(response);
      const { rows, total } = parseListPayload(payload);
      return {
        items: (rows || []).map(mapDocToTx).filter(Boolean),
        total,
      };
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      if (error.code !== 'ERR_CANCELED') {
        toast.error(translateErrorMessage(error));
      }
      return {
        items: [],
        total: 0,
      };
    }
  },
  {
    condition: (_, { getState }) => getState().transactionHistory.status !== 'pending',
  },
);

export const createTransactionHistory = createAsyncThunk(
  'transactionHistory/create',
  async (body, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      const response = await TransactionHistoryService.create(body);
      dispatch(slice.actions.handleStatus('success'));
      const doc = getSuccessPayload(response);
      const tx = mapDocToTx(doc);
      return { tx };
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      toast.error(translateErrorMessage(error));
      throw error;
    }
  },
);

export const updateTransactionHistory = createAsyncThunk(
  'transactionHistory/update',
  async ({ id, body }, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      const response = await TransactionHistoryService.update(id, body);
      dispatch(slice.actions.handleStatus('success'));
      const doc = getSuccessPayload(response);
      const tx = mapDocToTx(doc);
      return { tx };
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      toast.error(translateErrorMessage(error));
      throw error;
    }
  },
);

export const slice = createSlice({
  name: 'transactionHistory',
  initialState,
  reducers: {
    handleStatus: (state, action) => {
      state.status = action.payload;
    },
    clearAll: (state) => {
      state.history = {};
      state.status = 'idle';
    },
    patchTransactionStatus: (state, action) => {
      const { chain, hash, status, updatedAt } = action.payload;
      const list = state.history[chain];
      if (!list) return;
      const idx = list.findIndex((t) => t.hash === hash);
      if (idx < 0) return;
      list[idx] = {
        ...list[idx],
        status,
        updatedAt: updatedAt ? new Date(updatedAt) : new Date(),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactionHistory.fulfilled, (state, action) => {
        const { items } = action.payload;
        const historyByChain = {};
        items.forEach((item) => {
          if (!historyByChain[item.chain]) {
            historyByChain[item.chain] = [];
          }
          historyByChain[item.chain].push(item);
        });
        state.history = historyByChain;
      })
      .addCase(createTransactionHistory.fulfilled, (state, action) => {
        const { tx } = action.payload;
        if (!tx) return;
        const cur = state.history[tx.chain] || [];
        const withoutDup = cur.filter((t) => t.hash !== tx.hash);
        state.history[tx.chain] = [tx, ...withoutDup];
      })
      .addCase(updateTransactionHistory.fulfilled, (state, action) => {
        const { tx } = action.payload;
        const cur = state.history[tx.chain] || [];
        const withoutDup = cur.filter((t) => t.hash !== tx.hash);
        state.history[tx.chain] = [tx, ...withoutDup];
      });
  },
});

export default slice.reducer;
